import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Window, LoadingOverlay } from '@warmancer/ui';
import { authApi, getErrorMessage, DISCORD_URL } from '../api/client';
import styles from './AuthPages.module.css';

type VerifyState = 'verifying' | 'success' | 'error' | 'no-token';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'no-token');
  const [errorMessage, setErrorMessage] = useState('');

  const verifyMutation = useMutation<void, Error>({
    mutationFn: () => authApi.verifyEmail({ token: token! }),
    onSuccess: () => {
      setState('success');
    },
    onError: (error: Error) => {
      setState('error');
      setErrorMessage(getErrorMessage(error));
    },
  });

  useEffect(() => {
    if (token && state === 'verifying') {
      verifyMutation.mutate();
    }
  }, [token]);

  if (state === 'no-token') {
    return (
      <Window title="Email Verification" maxWidth={400} className={styles.window}>
        <div className={styles.errorMessage}>
          <p>No verification token provided.</p>
          <p>Please check your email for the verification link.</p>
          <p className={styles.helpContact}>Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</p>
          <Link to="/login" className={styles.successLink}>
            Return to Login
          </Link>
        </div>
      </Window>
    );
  }

  if (state === 'verifying') {
    return (
      <>
        <Window title="Verifying Email" maxWidth={400} className={styles.window}>
          <div className={styles.verifying}>
            <p>Please wait while we verify your email...</p>
          </div>
        </Window>
        <LoadingOverlay
          visible
          message="Verifying your email..."
          timeout={15000}
        />
      </>
    );
  }

  if (state === 'success') {
    return (
      <Window title="Email Verified" maxWidth={400} className={styles.window}>
        <div className={styles.successMessage}>
          <p>Your email has been successfully verified!</p>
          <p>You can now login to your account.</p>
          <Link to="/login" className={styles.successLink}>
            Go to Login
          </Link>
        </div>
      </Window>
    );
  }

  // Error state
  return (
    <Window title="Verification Failed" maxWidth={400} className={styles.window}>
      <div className={styles.errorMessage}>
        <p>{errorMessage}</p>
        <p>If you need a new verification email, please login and request a new one.</p>
        <p className={styles.helpContact}>Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</p>
        <Link to="/login" className={styles.successLink}>
          Return to Login
        </Link>
      </div>
    </Window>
  );
}
